import { ThunkAction } from 'redux-thunk';

import apiRequest from '@/services/apiRequest';
import { IBundleCampground, ICampgroundCategory } from '@/services/types/bundles/id';
import { getCoreApi } from '@/utility/getCoreApi';
import { logger } from '@/utility/logger';
import { IAction } from '@/utility/redux/action';

import { TRootState } from '../rootReducer';

const BUNDLES_LOADING = 'bundles/BUNDLES_LOADING';
const SET_BUNDLES = 'bundles/SET_BUNDLES';
const SET_IS_BUNDLES_LOADING = 'bundles/SET_IS_LOADING';

const CATEGORIES_LOADING = 'categories/BUNDLES_LOADING';
const SET_CATEGORIES = 'categories/SET_BUNDLES';
const SET_IS_CATEGORIES_LOADING = 'categories/SET_IS_LOADING';

interface ISetIsBundlesLoadingAction {
  type: typeof SET_IS_BUNDLES_LOADING;
  payload: boolean;
}
interface IRvBundlesLoadingAction extends IAction {
  type: typeof BUNDLES_LOADING;
}

interface ISetRvBundlesAction extends IAction {
  type: typeof SET_BUNDLES;
  payload: IBundleCampground[];
}

interface ISetIsCategoriesLoadingAction {
  type: typeof SET_IS_CATEGORIES_LOADING;
  payload: boolean;
}
interface ICategoriesLoadingAction extends IAction {
  type: typeof CATEGORIES_LOADING;
}

interface ISetCategoriesAction extends IAction {
  type: typeof SET_CATEGORIES;
  payload: ICampgroundCategory[];
}

type TAction =
  | IRvBundlesLoadingAction
  | ISetRvBundlesAction
  | ISetIsBundlesLoadingAction
  | ISetIsCategoriesLoadingAction
  | ICategoriesLoadingAction
  | ISetCategoriesAction;

const setBundles = (payload: IBundleCampground[]): ISetRvBundlesAction => ({
  type: SET_BUNDLES,
  payload,
});

const setCategories = (payload: ICampgroundCategory[]): ISetCategoriesAction => ({
  type: SET_CATEGORIES,
  payload,
});

export const getBundles =
  (
    offset = 0,
    limit = 0,
  ): ThunkAction<Promise<void>, TRootState, void, ISetRvBundlesAction | IRvBundlesLoadingAction> =>
  async (dispatch, _) => {
    dispatch<IRvBundlesLoadingAction>({
      type: BUNDLES_LOADING,
    });

    try {
      const response = await apiRequest<IBundleCampground[]>(
        {
          url: `${getCoreApi()}/landing-page-campgrounds?offset=${offset}&limit=${limit}`,
          method: 'GET',
        },
        true,
      );

      dispatch(setBundles(response));
    } catch (error) {
      logger.captureExceptionWithDatadog(error, { url: error.request?._currentUrl });
    }
  };

export const getBundlesByCategory =
  (
    category: string,
    offset = 0,
    limit = 0,
  ): ThunkAction<Promise<void>, TRootState, void, ISetRvBundlesAction | IRvBundlesLoadingAction> =>
  async (dispatch, _) => {
    dispatch<IRvBundlesLoadingAction>({
      type: BUNDLES_LOADING,
    });

    try {
      const response = await apiRequest<IBundleCampground[]>(
        {
          url: `${getCoreApi()}/landing-page-campgrounds?offset=${offset}&limit=${limit}&filter[categories]=${category}`,
          method: 'GET',
        },
        true,
      );

      dispatch(setBundles(response));
    } catch (error) {
      logger.captureExceptionWithDatadog(error, { url: error.request?._currentUrl });
    }
  };

export const getCampgroundCategories =
  (): ThunkAction<
    Promise<void>,
    TRootState,
    void,
    ISetCategoriesAction | ICategoriesLoadingAction
  > =>
  async (dispatch, _) => {
    dispatch<ICategoriesLoadingAction>({
      type: CATEGORIES_LOADING,
    });

    try {
      const response = await apiRequest<ICampgroundCategory[]>(
        {
          url: `${getCoreApi()}/campground-categories`,
          method: 'GET',
        },
        true,
      );

      dispatch(setCategories(response));
    } catch (error) {
      logger.captureExceptionWithDatadog(error, { url: error.request?._currentUrl });
    }
  };

interface IStateCampgrounds {
  data: IBundleCampground[] | null;
  isLoading: boolean;
}

interface IStateCampgroundsCategories {
  data: ICampgroundCategory[] | null;
  isLoading: boolean;
}

interface IState {
  campgrounds: IStateCampgrounds;
  categories: IStateCampgroundsCategories;
}

export const initialState: IState = {
  campgrounds: {
    data: null,
    isLoading: true,
  },
  categories: {
    data: null,
    isLoading: true,
  },
};

export default function reducer(state = initialState, action: TAction): IState {
  switch (action.type) {
    case BUNDLES_LOADING:
      return {
        ...state,
        campgrounds: {
          data: null,
          isLoading: true,
        },
      };
    case SET_BUNDLES:
      return {
        ...state,
        campgrounds: {
          data: action.payload,
          isLoading: false,
        },
      };
    case SET_IS_BUNDLES_LOADING:
      return {
        ...state,
        campgrounds: {
          isLoading: action.payload,
          data: null,
        },
      };
    case CATEGORIES_LOADING:
      return {
        ...state,
        categories: {
          data: null,
          isLoading: true,
        },
      };
    case SET_CATEGORIES:
      return {
        ...state,
        categories: {
          data: action.payload,
          isLoading: false,
        },
      };
    case SET_IS_CATEGORIES_LOADING:
      return {
        ...state,
        categories: {
          isLoading: action.payload,
          data: null,
        },
      };
    default:
      return { ...state };
  }
}
